import { useMemo, useState } from "react";
import { useFormik } from "formik";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  FormControl,
  Box,
  Alert,
  AlertTitle,
} from "@mui/material";
import {
  AdminAllLocationsCustomerResDto,
  AdminPostTransferLocationsToRouteReqDto,
} from "service/api/estabrookSchemas";
import { DialogProps } from "@mui/material";
import { toFormikValidationSchema } from "zod-formik-adapter";
import {
  adminPostTransferLocationsToRouteReqDtoSchema,
  useAdminRouteV2ControllerTransferLocationToRoute,
  useAdminRouteV2ControllerAddUnassignedLocationsToRoute,
} from "service";
import {
  ControlledLabelSelectOption,
  ControlledSelect,
  ControlledSelectOption,
  makeSxStyles,
} from "ui";
import useRoutePlannerStore, {
  MoveOrders,
  Modals,
  PendingUnassignedMoveValues,
} from "../../../../state/route-planner-store";
import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import { capitalizeFirstLetter } from "../../../../utils/string-helpers";
import CustomStepper from "./stepper";
import { MapMarker, OrderStatus } from "../../../../components/map/map.types";
import CreateSequenceModal from "../create-sequence-modal";
import { hasInProgressLocations } from "../../../../utils/route-planner";
import { isUnassignedAddress } from "../../../../shared/utils/address-assignment";
import { queryClient } from "../../../../query-client";

export interface MoveLocationsDialogProps
  extends Omit<
    DialogProps,
    "onClose" | "open" | "style" | "inputValue" | "handler"
  > {
  onClose: () => void;
  onRefresh: () => void;
  open?: boolean;
  inputValue?: string;
  handler?: (value: string) => void;
  shouldSequence: boolean;
  invalidSelection?: boolean;
  defaultActiveStep?: number;
}

interface FieldOptions {
  key: keyof AdminPostTransferLocationsToRouteReqDto;
  overridenFormValue?: string | undefined;
  overrideFormValue?: boolean;
  label: string;
  placeholder: string;
  options: ControlledSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
}

const MoveLocationsDialog: React.FC<MoveLocationsDialogProps> = (props) => {
  const { date } = useParams();
  const selectedDate = date
    ? DateTime.fromFormat(date, "MM-dd-yyyy")
    : DateTime.now();
  const {
    onClose,
    onRefresh,
    open,
    shouldSequence,
    invalidSelection = false,
    defaultActiveStep = -1,
    ...rest
  } = props;
  const {
    routes,
    selectedAddresses,
    moveOrders,
    setMoveOrders,
    resetAllSelected,
    selectedAddressesForSequencing,
    setSelectedAddressesForSequencing,
    setMapCentering,
    setAllSequenced,
    allSequenced,
    setIsLoadingModalShown,
    setSidebar,
    setActiveModal,
    setPendingUnassignedMove,
  } = useRoutePlannerStore();
  const styles = useStyles();
  const [shift, setShift] = useState<string | undefined>("");
  const [sequence, setSequence] = useState<string | undefined>("");
  const [activeStep, setActiveStep] = useState<number>(defaultActiveStep);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setActiveStep(defaultActiveStep);

    if (defaultActiveStep === 1) {
      setSidebar(true);
    } else {
      setSidebar(false);
    }

    return () => {
      setActiveStep(-1);
      setSidebar(false);
    };
  }, [defaultActiveStep]);

  const handleClose = () => {
    setAllSequenced(false);
    // Keep the sidebar in sync with the selection. Cancelling the dialog must
    // not orphan the selection (markers still highlighted but no sidebar to
    // act on them). If addresses are still selected, the sidebar stays open.
    setSidebar(selectedAddresses.length > 0);
    onClose();
  };

  const { values, setFieldValue, setFieldTouched, errors, touched, resetForm } =
    useFormik<AdminPostTransferLocationsToRouteReqDto>({
      initialValues: {
        targetRouteId: "",
        targetDate: selectedDate.toFormat("yyyy-MM-dd"),
        targetSeqNumber: 1,
        targetRouteOrderStatus: "regular",
        transferMode: "before",
        locationIds: [],
      },
      enableReinitialize: true,
      validationSchema: toFormikValidationSchema(
        adminPostTransferLocationsToRouteReqDtoSchema,
      ),
      onSubmit: (values) => {
        submitForm(values);
      },
      validateOnBlur: false,
      validateOnChange: false,
    });

  const prepareOrdersForResequence = (futureTargetSeqNumber: number) => {
    const targetRoute = routes.find(
      (route) => route.id === values.targetRouteId,
    );

    if (!targetRoute) return;

    const targetRouteShiftLocations = targetRoute.locations?.filter(
      (loc) => loc.order?.status === shift,
    );

    let resequencedTargetRouteLocations: MapMarker[] | null = null;
    let mapMarkerLocation: MapMarker | null = null;

    resequencedTargetRouteLocations =
      targetRouteShiftLocations?.map((location) => {
        mapMarkerLocation = {
          ...location,
          id: location.locationId,
          latitude: location.latitude,
          longitude: location.longitude,
          order: location.order as OrderStatus,
          region: targetRoute.region,
          customer: location.customer as AdminAllLocationsCustomerResDto,
        };

        if (location.locationSequence >= futureTargetSeqNumber) {
          return {
            ...mapMarkerLocation,
            locationSequence:
              location.locationSequence + selectedAddresses.length,
          };
        }

        return mapMarkerLocation;
      }) || [];

    if (!resequencedTargetRouteLocations) return;

    const routeWithLocationsToResequence: MapMarker[] = [
      ...resequencedTargetRouteLocations,
      ...selectedAddresses.map((address) => ({
        ...address,
        locationSequence: null,
        region: {
          id: "unassigned",
          name: "Unassigned",
          color: "unassigned",
        },
        order: {
          ...address.order,
          status: "regular",
        } as OrderStatus,
      })),
    ];

    // Only sequence the newly added addresses (selectedAddresses), not the existing route addresses
    const addressesToSequence = selectedAddresses.map((address) => ({
      ...address,
      locationSequence: null,
      selected: false,
      region: {
        id: "unassigned",
        name: "Unassigned",
        color: "unassigned",
      },
    }));

    setMapCentering(undefined, routeWithLocationsToResequence);
    setSelectedAddressesForSequencing(
      addressesToSequence,
      futureTargetSeqNumber,
    );

    setSidebar(true);
  };

  const routeOptions: ControlledLabelSelectOption[] = routes.map((route) => ({
    value: route.id,
    label: route.region?.name || route.routeName,
  }));

  const addressOptions: ControlledLabelSelectOption[] = useMemo(() => {
    const targetRoute = routes.find(
      (route) => route.id === values.targetRouteId,
    );

    if (!targetRoute) return [];

    return (
      targetRoute.locations
        ?.filter((loc) => loc.order?.status === shift)
        .map((loc) => ({
          value: loc.locationSequence.toString(),
          label: `${loc.locationSequence}. ${loc.address}`,
        })) || []
    );
  }, [shift, values.targetRouteId, routes]);

  // Determine if all selected addresses are unassigned
  const allSelectedAddressesUnassigned =
    selectedAddresses.every(isUnassignedAddress);

  const { mutateAsync: transferOrders } =
    useAdminRouteV2ControllerTransferLocationToRoute({
      onSuccess: () => {
        toast.success(generateToastMessageForTransfer(moveOrders));
        onRefresh();
        resetAllSelected();
      },
    });

  const { mutateAsync: addUnassignedLocationsToRoute } =
    useAdminRouteV2ControllerAddUnassignedLocationsToRoute({
      // No success handler here - will be handled by the confirmation modal
    });

  useEffect(() => {
    setSequence("");
  }, [values.targetRouteId]);

  useEffect(() => {
    if (!open) {
      setShift("");
      setSequence("");
      resetForm();
      // Restore the sidebar to match the current selection when the dialog
      // closes, so a remaining selection is still actionable.
      setSidebar(selectedAddresses.length > 0);
    } else if (activeStep === 1) {
      setSidebar(true);
    }
  }, [open, resetForm, activeStep, selectedAddresses.length]);

  useEffect(() => {
    if (!open || activeStep === 1 || values.targetRouteId) return;

    // Pre-fill the target route/region with the selection's original route
    // (e.g. when restoring a skipped order) so the admin isn't asked to
    // re-pick a route they already had.
    const originalRouteIds = Array.from(
      new Set(selectedAddresses.map((address) => address.routeId)),
    ).filter((routeId): routeId is string => Boolean(routeId));

    if (originalRouteIds.length === 1) {
      setFieldValue("targetRouteId", originalRouteIds[0]);
      setFieldTouched("targetRouteId", true);
    }
  }, [
    open,
    activeStep,
    selectedAddresses,
    values.targetRouteId,
    setFieldValue,
    setFieldTouched,
  ]);

  const fields: FieldOptions[] = [
    {
      key: "targetRouteId",
      label: `Move ${selectedAddresses.length} addresses to route`,
      placeholder: "Select Region",
      options: routeOptions,
      onChange: (value) => {
        setFieldValue("targetRouteId", value);
        setFieldTouched("targetRouteId", true);
      },
    },
    {
      key: "targetRouteOrderStatus",
      overridenFormValue: shift,
      overrideFormValue: true,
      label: "in",
      placeholder: "Select shift",
      options: shiftOptions,
      onChange: (value) => {
        setShift(value);
        setFieldTouched("targetRouteOrderStatus", true);
      },
    },
    {
      key: "transferMode",
      overrideFormValue: undefined,
      label: "",
      placeholder: "Before",
      options: placementOptions,
      onChange: (value) => {
        setFieldValue("transferMode", value);
        setFieldTouched("transferMode", true);
      },
      disabled: !addressOptions.length,
    },
    {
      key: "targetSeqNumber",
      overridenFormValue: sequence,
      overrideFormValue: true,
      label: "Address",
      placeholder: "Select Address",
      options: addressOptions,
      onChange: (value) => {
        setSequence(value);
        setFieldTouched("targetSeqNumber", true);
      },
      disabled: !addressOptions.length,
    },
  ];

  const showLoadingScreen = () => {
    setIsLoadingModalShown(true);
    onClose();
  };

  const hideModal = () => {
    setIsLoadingModalShown(false);
    setIsSubmitting(false);
  };

  const submitForm = async (
    values: AdminPostTransferLocationsToRouteReqDto,
  ) => {
    let modifiedValues = { ...values };
    setIsSubmitting(true);

    const shouldExcludeInProgress = hasInProgressLocations(selectedAddresses);

    const filteredAddresses = shouldExcludeInProgress
      ? selectedAddresses.filter(
          (address) => address.driverStatus !== "inProgress",
        )
      : selectedAddresses;

    if (!allSequenced) {
      const sortedSelectedAddresses = filteredAddresses.sort(
        (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0),
      );
      const selectedSeqNunber = Number(sequence) === 0 ? 1 : Number(sequence);

      // For regular transfer endpoint: frontend adds 1 for "after"
      // For unassigned endpoint: backend adds 1 for "after", so we send as-is
      const targetSeqNumberForTransfer =
        values.transferMode === "before"
          ? selectedSeqNunber
          : selectedSeqNunber + 1;
      const targetSeqNumberForUnassigned = selectedSeqNunber;

      let futureTargetSeqNumber = targetSeqNumberForTransfer;

      const locationIds = sortedSelectedAddresses.map((address) => ({
        locationId: address.id,
        seqNumber: futureTargetSeqNumber++,
      }));

      modifiedValues = {
        ...values,
        targetSeqNumber: targetSeqNumberForTransfer,
        targetRouteOrderStatus: shift as "regular" | "delayed" | "skipped",
        locationIds,
        transferMode: values.transferMode,
      };

      const selectedRoute = routes.find(
        (route) => route.id === values.targetRouteId,
      );

      setMoveOrders({
        ...modifiedValues,
        targetRouteName:
          selectedRoute?.region?.name ?? selectedRoute?.routeName ?? "",
        visibleTargetSequence: targetSeqNumberForTransfer,
      });

      if (shouldSequence) {
        setActiveStep(activeStep + 1);
        prepareOrdersForResequence(targetSeqNumberForTransfer);
        setIsSubmitting(false);
      } else {
        if (allSelectedAddressesUnassigned) {
          // Show confirmation modal for unassigned addresses
          // Use targetSeqNumberForUnassigned because unassigned endpoint backend adds 1 for "after"
          let futureTargetSeqNumberUnassigned = targetSeqNumberForUnassigned;
          const locationIdsUnassigned = sortedSelectedAddresses.map(
            (address) => ({
              locationId: address.id,
              seqNumber: futureTargetSeqNumberUnassigned++,
            }),
          );

          const pendingMove: PendingUnassignedMoveValues = {
            moveOrders: {
              ...modifiedValues,
              targetSeqNumber: targetSeqNumberForUnassigned,
              locationIds: locationIdsUnassigned,
              targetRouteName:
                selectedRoute?.region?.name ?? selectedRoute?.routeName ?? "",
              visibleTargetSequence: targetSeqNumberForUnassigned,
            },
            selectedDate: selectedDate.toFormat("yyyy-MM-dd"),
            mutationFn: addUnassignedLocationsToRoute,
            onSuccess: () => {
              toast.success(
                generateToastMessageForTransfer({
                  ...modifiedValues,
                  targetSeqNumber: targetSeqNumberForUnassigned,
                  locationIds: locationIdsUnassigned,
                  targetRouteName:
                    selectedRoute?.region?.name ??
                    selectedRoute?.routeName ??
                    "",
                  visibleTargetSequence: targetSeqNumberForUnassigned,
                }),
              );
              resetAllSelected();
              onRefresh(); // Invalidate route planner
              // Also invalidate unassigned locations query
              queryClient.invalidateQueries({
                queryKey: [
                  "adminLocationV2ControllerGetUnassignedRouteLocationsForDatePaginated",
                ],
              });
            },
          };
          setPendingUnassignedMove(pendingMove);
          setActiveModal(Modals.CONFIRM_MOVE_UNASSIGNED_TO_ROUTE);
          onClose(); // Close the dialog immediately
          setIsSubmitting(false);
        } else {
          try {
            showLoadingScreen();

            await transferOrders({
              body: { ...modifiedValues },
              pathParams: {
                date: selectedDate.toFormat("yyyy-MM-dd"),
              },
            });
          } finally {
            hideModal();
          }
        }
      }
    } else {
      if (allSelectedAddressesUnassigned) {
        // Show confirmation modal for unassigned addresses in sequencing mode
        // Adjust targetSeqNumber for unassigned: backend adds 1 for "after", so subtract 1 if "after"
        const adjustedTargetSeqNumber =
          moveOrders.transferMode === "after"
            ? moveOrders.targetSeqNumber - 1
            : moveOrders.targetSeqNumber;

        const finalMoveOrders = {
          targetDate: values.targetDate,
          targetRouteId: moveOrders.targetRouteId,
          targetRouteName: moveOrders.targetRouteName,
          targetRouteOrderStatus: moveOrders.targetRouteOrderStatus,
          targetSeqNumber: adjustedTargetSeqNumber,
          transferMode: moveOrders.transferMode,
          locationIds: filteredAddresses.map((address: MapMarker) => {
            const sequencedAddress = selectedAddressesForSequencing.find(
              (seqAddress) => seqAddress.id === address.id,
            );

            return {
              locationId: sequencedAddress?.id as string,
              seqNumber: sequencedAddress?.locationSequence as number,
            };
          }),
          visibleTargetSequence: adjustedTargetSeqNumber,
        };

        const pendingMove: PendingUnassignedMoveValues = {
          moveOrders: finalMoveOrders,
          selectedDate: selectedDate.toFormat("yyyy-MM-dd"),
          mutationFn: addUnassignedLocationsToRoute,
          onSuccess: () => {
            toast.success(generateToastMessageForTransfer(finalMoveOrders));
            resetAllSelected();
            onRefresh(); // Invalidate route planner
            // Also invalidate unassigned locations query
            queryClient.invalidateQueries({
              queryKey: [
                "adminLocationV2ControllerGetUnassignedRouteLocationsForDatePaginated",
              ],
            });
          },
        };
        setPendingUnassignedMove(pendingMove);
        setActiveModal(Modals.CONFIRM_MOVE_UNASSIGNED_TO_ROUTE);
        onClose(); // Close the dialog immediately
      } else {
        try {
          showLoadingScreen();

          await transferOrders({
            body: {
              targetDate: values.targetDate,
              targetRouteId: moveOrders.targetRouteId,
              targetRouteOrderStatus: moveOrders.targetRouteOrderStatus,
              targetSeqNumber: moveOrders.targetSeqNumber,
              transferMode: moveOrders.transferMode,
              locationIds: filteredAddresses.map((address: MapMarker) => {
                const sequencedAddress = selectedAddressesForSequencing.find(
                  (seqAddress) => seqAddress.id === address.id,
                );

                return {
                  locationId: sequencedAddress?.id as string,
                  seqNumber: sequencedAddress?.locationSequence as number,
                };
              }),
            },
            pathParams: {
              date: selectedDate.toFormat("yyyy-MM-dd"),
            },
          });
        } finally {
          hideModal();
        }
      }
    }

    setAllSequenced(false);
  };

  const enabledConfirmButton =
    activeStep === 1
      ? allSequenced
      : (values.targetRouteId !== "" &&
          shift !== undefined &&
          shift.length > 0 &&
          (addressOptions.length > 0
            ? sequence !== undefined && sequence.length > 0
            : values.targetRouteId !== "")) ||
        !isSubmitting;

  return (
    <Dialog
      onClose={handleClose}
      {...rest}
      sx={
        activeStep === 1 ? styles.dialogWrapperFullWidth : styles.dialogWrapper
      }
      open={open || false}
    >
      <Box sx={styles.dialogTitleWrapper}>
        <DialogTitle variant="titleLarge" sx={styles.dialogTitle}>
          Move Addresses
        </DialogTitle>

        {shouldSequence && (
          <CustomStepper steps={[1, 2]} activeStep={activeStep} />
        )}
      </Box>
      <DialogContent>
        {invalidSelection && activeStep !== 1 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle data-testid="invalid-selection-header">
              Incorrect selection of addresses!
            </AlertTitle>
            <span data-testid="invalid-selection-body">
              Selected addresses that do not belong to the same shift and route
              and do not follow a sequence, need to be re-sequenced before
              moving to a new or same route/shift
            </span>
          </Alert>
        )}
        <form>
          {activeStep === 1 ? (
            <CreateSequenceModal />
          ) : (
            fields.map((field) => (
              <Box key={field.key} sx={styles.fieldContainer}>
                <InputLabel sx={styles.inputLabel}>{field.label}</InputLabel>
                <FormControl sx={styles.formControl}>
                  <ControlledSelect
                    defaultValue={""}
                    id={field.key}
                    name={field.key}
                    placeholderSelectEnabled={false}
                    value={
                      field.overrideFormValue
                        ? field.overridenFormValue
                        : values[field.key].toString()
                    }
                    variant="outlined"
                    placeholder={field.placeholder}
                    options={field.options}
                    onChange={(value) => field.onChange(value)}
                    error={touched[field.key] && Boolean(errors[field.key])}
                    disabled={field.disabled || false}
                    MenuProps={{
                      sx: { zIndex: 1500 },
                    }}
                  />
                </FormControl>
              </Box>
            ))
          )}
          <DialogActions sx={styles.dialogActions}>
            <Button
              type="button"
              onClick={handleClose}
              sx={styles.cancelButton}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={styles.confirmButton}
              disabled={!enabledConfirmButton}
              data-testid="confirm-button"
              onClick={async () => await submitForm(values)}
            >
              {shouldSequence ? "Continue" : "Move Addresses"}
            </Button>
          </DialogActions>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const useStyles = makeSxStyles(({ palette: { grey, primary } }) => ({
  dialogWrapper: {
    zIndex: 1400,
    "& .MuiPaper-root": {
      maxWidth: "900px",
    },
  },
  dialogWrapperFullWidth: {
    zIndex: 1400,
    "& .MuiPaper-root": {
      maxWidth: "95vw",
      width: "95vw",
      height: "90vh",
      maxHeight: "90vh",
    },
  },
  dialogTitle: {
    color: grey[100],
    lineHeight: "28px",
  },
  dialogTitleWrapper: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
  dialogActions: {
    marginTop: "40px",
  },
  cancelButton: {
    color: primary.main,
  },
  confirmButton: {
    backgroundColor: primary.main,
    color: "#00344E",
  },
  colorContainer: {
    display: "flex",
    alignItems: "center",
  },
  colorBox: {
    width: 6,
    height: 16,
    marginRight: "4px",
    borderRadius: "2px",
  },
  fieldContainer: {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
  },
  inputLabel: {
    flex: 1,
    paddingRight: "12px",
  },
  formControl: {
    flex: 1,
    width: "210px",
  },
}));

export const generateToastMessageForTransfer = (moveOrders: MoveOrders) => {
  const count = moveOrders.locationIds.length;
  const addressWord = count === 1 ? "Address" : `${count} Addresses`;
  const verb = count === 1 ? "has" : "have";
  return `${addressWord} ${verb} been moved to ${moveOrders.targetRouteName} Route ${capitalizeFirstLetter(moveOrders.targetRouteOrderStatus)}.`;
};

const shiftOptions: ControlledLabelSelectOption[] = [
  {
    label: "Regular",
    value: "regular",
  },
  {
    label: "Delayed",
    value: "delayed",
  },
  {
    label: "Skipped",
    value: "skipped",
  },
];

const placementOptions: ControlledLabelSelectOption[] = [
  {
    label: "Before",
    value: "before",
  },
  {
    label: "After",
    value: "after",
  },
];

export default MoveLocationsDialog;
